import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VenueBottomNav from '../../components/VenueBottomNav';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatEuro = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  });
};

// Polished success badge: a soft green disc with a crisp white check.
// Replaces the previous hand-drawn stroke. Animated scale-in for a small
// moment of celebration when the page lands.
const SuccessBadge = () => (
  <motion.div
    initial={{ scale: 0.4, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.05 }}
    style={{
      width: 76, height: 76, borderRadius: '50%',
      backgroundColor: 'var(--status-success-text)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 14px 30px rgba(31, 142, 42, 0.28)',
    }}
  >
    <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 19.5L16 26.5L29 12.5"
        stroke="#FFFFFF"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </motion.div>
);

function HeadlineRow({ label, value, valueColor, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{
        fontSize: big ? 16 : 14,
        color: 'var(--text-2)',
        fontWeight: big ? 600 : 500,
      }}>{label}</span>
      <span style={{
        fontFamily: "'Inria Serif', serif",
        fontSize: big ? 26 : 18,
        fontWeight: big ? 700 : 600,
        color: valueColor || 'var(--text)',
      }}>{value}</span>
    </div>
  );
}

function DetailRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{label}</span>
      <span style={{
        fontFamily: "'Inria Serif', serif",
        fontSize: 14,
        fontWeight: 500,
        color: valueColor || 'var(--text)',
      }}>{value}</span>
    </div>
  );
}

export default function VenueTransactionDetails() {
  const { txnId } = useParams();
  const navigate = useNavigate();
  const [txn, setTxn] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchTransaction = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/transactions/${txnId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTxn(res.data);
    } catch (err) {
      console.error('Failed to load transaction:', err);
    }
  }, [txnId]);

  const handleDownloadPdf = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/transactions/${txnId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `allure-receipt-${(txnId || '').slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Give the browser a beat to start the download before revoking.
      setTimeout(() => window.URL.revokeObjectURL(url), 1200);
      toast.success('Receipt PDF downloaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not download receipt');
    } finally {
      setDownloading(false);
    }
  }, [downloading, txnId]);

  useEffect(() => {
    fetchTransaction();
  }, [fetchTransaction]);

  if (!txn) {
    return (
      <div className="app-shell" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--app-bg)' }}>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, color: 'var(--text-3)' }}>Loading…</p>
      </div>
    );
  }

  // What the venue _paid_ in fees on this transaction. Host referral
  // commission is folded into the Allure fee from the venue's perspective —
  // it's a platform-side accounting split (Allure forwards a share of its
  // fee to the referring host) and the venue shouldn't have to think about
  // that split.
  const allureFeeTotal = Number(txn.app_fee || 0) + Number(txn.host_commission || 0);
  const feesPaid = Number(txn.viva_wallet_fee || 0) + allureFeeTotal;

  return (
    <div className="app-shell" data-testid="venue-transaction-details" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      paddingBottom: 140,
      paddingLeft: 24, paddingRight: 24,
      position: 'relative',
    }}>
      {/* Top bar with download — sits inside the safe-area top padding on
          .app-shell so the button never lives under the device notch. */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          display: 'flex',
          justifyContent: 'flex-end',
          paddingTop: 14,
        }}
      >
        <button
          type="button"
          data-testid="txn-download-btn"
          aria-label="Download receipt"
          onClick={handleDownloadPdf}
          disabled={downloading}
          style={{
            background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 10,
            padding: '8px 10px', cursor: downloading ? 'wait' : 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            opacity: downloading ? 0.65 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: "'Inria Serif', serif", fontSize: 13, fontWeight: 600,
            color: 'var(--accent-purple)',
          }}
        >
          <Download size={18} color="var(--accent-purple)" />
          {downloading ? 'Preparing…' : 'PDF'}
        </button>
      </div>

      {/* Logo + success badge */}
      <div style={{ marginTop: 16 }}>
        <RouteTransitionLogo width={110} height={98} dataTestId="venue-transaction-details-shared-logo" />
      </div>
      <div style={{ marginTop: 22 }}>
        <SuccessBadge />
      </div>

      <h2 style={{
        fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700,
        color: 'var(--text)', textAlign: 'center', marginTop: 18, marginBottom: 4,
      }}>
        Payment received
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0 }}>
        Customer ID · <span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{txn.customer_id || '—'}</span>
      </p>

      {/* Headline card — three numbers the venue actually wants to see */}
      <div data-testid="txn-headline-card" style={{
        marginTop: 22,
        width: '100%', maxWidth: 360,
        background: 'var(--surface)',
        border: '1px solid var(--card-border)',
        borderRadius: 22,
        padding: 20,
        boxShadow: '0 8px 24px rgba(0,0,0,0.04)',
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <HeadlineRow label="Profit" value={formatEuro(txn.venue_profit)} valueColor="var(--status-success-text)" big />
        <HeadlineRow label="Fees paid" value={formatEuro(feesPaid)} />
        <HeadlineRow
          label="Staff tips"
          value={txn.tip_amount > 0 ? formatEuro(txn.tip_amount) : '—'}
          valueColor={txn.tip_amount > 0 ? 'var(--status-success-text)' : 'var(--text-3)'}
        />

        {/* Expand toggle */}
        <button
          type="button"
          data-testid="txn-expand-btn"
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginTop: 4,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--accent-purple)', fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            padding: '6px 0',
          }}
        >
          {expanded ? 'Hide details' : 'See full breakdown'}
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: 'inline-flex' }}
          >
            <ChevronDown size={16} />
          </motion.span>
        </button>

        {/* Expanded details */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="txn-expanded"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: 'hidden' }}
              data-testid="txn-expanded-details"
            >
              <div style={{ height: 1, background: 'var(--hairline)', margin: '4px 0 12px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <DetailRow label="Original bill" value={formatEuro(txn.original_amount)} />
                <DetailRow
                  label={`Allure discount · ${Math.round(txn.discount_percent || 0)}%`}
                  value={`− ${formatEuro((txn.original_amount || 0) - (txn.discounted_amount || 0))}`}
                  valueColor="var(--status-success-text)"
                />
                <DetailRow label="Customer paid" value={formatEuro(txn.discounted_amount)} />
                <div style={{ height: 1, background: 'var(--hairline)', margin: '2px 0' }} />
                <DetailRow label="Viva Wallet fee" value={`− ${formatEuro(txn.viva_wallet_fee)}`} />
                <DetailRow label="Allure fee" value={`− ${formatEuro(allureFeeTotal)}`} />
                <div style={{ height: 1, background: 'var(--hairline)', margin: '2px 0' }} />
                <DetailRow label="Net to your account" value={formatEuro(txn.venue_profit)} valueColor="var(--status-success-text)" />
                {txn.tip_amount > 0 && (
                  <DetailRow
                    label={`Staff tip (${txn.tip_percent}%) — passes through to staff`}
                    value={`+ ${formatEuro(txn.tip_amount)}`}
                    valueColor="var(--status-success-text)"
                  />
                )}
                {txn.payment_reference && (
                  <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '6px 0 0', textAlign: 'right' }}>
                    Ref: <span style={{ fontFamily: 'monospace' }}>{txn.payment_reference}</span>
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* New scan CTA */}
      <button
        type="button"
        data-testid="txn-new-scan-btn"
        onClick={() => navigate('/venue/scan')}
        style={{
          marginTop: 22,
          width: '100%', maxWidth: 360, height: 50,
          background: 'var(--solid-cta-bg)', color: 'var(--solid-cta-text)',
          border: 'none', borderRadius: 999,
          fontSize: 15, fontWeight: 600,
          cursor: 'pointer',
          boxShadow: '0 8px 22px rgba(0,0,0,0.18)',
        }}
      >
        New scan
      </button>

      <VenueBottomNav />
    </div>
  );
}
